import { v4 as uuidv4 } from 'uuid'
import { useContext, useLayoutEffect, useRef } from 'react'
import { PanelContext } from '../../../components'
import { useCompleteLocationSoFar } from '../../../hooks'
import { getPanelTop } from '../../../service'
import { useRunOnce } from '../use-run-once'
import { Store } from '../../../store'
import { RouterValueListeners } from './listeners'
import { findLast } from '../../iteratees'

type ValueGetter = () => any

const DELIMITER_CHAR = '$'

interface RouterValue {
  invalid: boolean
  activatedAt?: number
  value: any
}

const valueStores: Record<string, Record<string, RouterValue>> = {}

window.addEventListener('keyup', e => {
  if (e.code === 'KeyV')
    console.log('router values:', JSON.parse(JSON.stringify(valueStores)))
})

export function getRouterValue(valueName: string, panel: string) {
  if (!valueStores[valueName]) return

  // TODO: check for registered functions in panel's child panels
  const panelChildren = Store.getPanelChildPanels(panel)

  // NOTE: We have to choose the last valid value. There is no way to determine what child-panel is active for now
  const childrenRouterValue = findLast(
    panelChildren.map(child => getRouterValue(valueName, child)),
    Boolean,
  )

  if (childrenRouterValue) return childrenRouterValue

  const topLocation = getPanelTop(panel)?.location
  const locationRegisteredKeys = Object.keys(valueStores[valueName])
    .filter(key => {
      if (valueStores[valueName][key].invalid) return false

      const [registeredPanel, registeredLocation] = key.split(DELIMITER_CHAR)
      return (
        registeredPanel === panel && topLocation?.startsWith(registeredLocation)
      )
    })
    .sort((a, b) => {
      const aLocation = a.split(DELIMITER_CHAR)[1]
      const bLocation = b.split(DELIMITER_CHAR)[1]

      return bLocation.length - aLocation.length
    })

  const activeRouterValue = locationRegisteredKeys[0]
  return activeRouterValue
    ? valueStores[valueName][activeRouterValue].value
    : undefined
}

function isLocationSubsetOfAnother(locationA: string, locationB: string) {
  const splitLocationA = locationA.split('/')
  const splitLocationB = locationB.split('/')

  if (splitLocationA.length > splitLocationB.length) return false
  let isSubset = true
  for (let i = 0; i < splitLocationA.length; i++) {
    const itemA = splitLocationA[i]
    const itemB = splitLocationB[i]

    if (itemA !== itemB) {
      isSubset = false
      break
    }
  }

  return isSubset
}

function activateLastInvalidatedItem(
  valueName: string,
  panel: string,
  location: string,
  id: string,
) {
  if (!valueStores[valueName]) return
  const keys = Object.keys(valueStores[valueName])

  const itemKeyToActivate = keys
    .filter(key => {
      const [registeredPanel, registeredLocation, uid] = key.split(
        DELIMITER_CHAR,
      )

      const { invalid } = valueStores[valueName][key]
      return (
        invalid &&
        uid !== id &&
        registeredPanel === panel &&
        isLocationSubsetOfAnother(location, registeredLocation)
      )
    })
    .sort((keyA, keyB) => {
      const { activatedAt: timeA } = valueStores[valueName][keyA]
      const { activatedAt: timeB } = valueStores[valueName][keyB]

      return timeB - timeA
    })[0]

  if (valueStores[valueName][itemKeyToActivate]) {
    valueStores[valueName][itemKeyToActivate].invalid = false
    valueStores[valueName][itemKeyToActivate].activatedAt = Date.now()
  }
}

function invalidateOtherRegisteredItems(
  valueName: string,
  panel: string,
  location: string,
  id: string,
) {
  if (!valueStores[valueName]) return
  const keys = Object.keys(valueStores[valueName])

  keys.forEach(key => {
    const [registeredPanel, registeredLocation, uid] = key.split(DELIMITER_CHAR)

    if (
      uid !== id &&
      registeredPanel === panel &&
      isLocationSubsetOfAnother(registeredLocation, location)
    ) {
      valueStores[valueName][key].invalid = true
      valueStores[valueName][key].activatedAt = undefined
    }
  })
}

export function useSetRouterValue(
  valueName: string,
  valueGetter: ValueGetter,
  deps: any[],
  isActive = true,
) {
  const location = useCompleteLocationSoFar()

  const uidRef = useRef<string>()
  useRunOnce(() => {
    uidRef.current = uuidv4()
    if (!valueStores[valueName]) valueStores[valueName] = {}
  })

  const { name: panelName } = useContext(PanelContext)
  const hashKey = [panelName, location, uidRef.current].join(DELIMITER_CHAR)

  useLayoutEffect(() => {
    return () => {
      if (valueStores[valueName]) delete valueStores[valueName][hashKey]
    }
  }, [])

  useLayoutEffect(() => {
    if (!isActive) {
      if (valueStores[valueName]?.[hashKey]) {
        valueStores[valueName][hashKey].invalid = true
        activateLastInvalidatedItem(
          valueName,
          panelName,
          location,
          uidRef.current,
        )
      }

      RouterValueListeners.notify(valueName)
      return
    }

    const prevIsActive = !valueStores[valueName]?.[hashKey]?.invalid
    const prevActivatedAt = valueStores[valueName]?.[hashKey]?.activatedAt

    valueStores[valueName][hashKey] = {
      value: valueGetter(),
      invalid: false,
      activatedAt: prevIsActive ? prevActivatedAt : Date.now(),
    }

    invalidateOtherRegisteredItems(
      valueName,
      panelName,
      location,
      uidRef.current!,
    )

    RouterValueListeners.notify(valueName)
  }, [isActive, ...deps])
}
